import { useRef } from 'react';
import type { DateRangePickerProps as AriaDateRangePickerProps, DateValue, ValidationResult } from 'react-aria-components';
import { DateRangePicker as AriaDateRangePicker, Button, CalendarCell, CalendarGrid, CalendarGridHeader, CalendarHeaderCell, CalendarGridBody, DateInput, DateSegment, Dialog, FieldError, Heading, Label, Popover, RangeCalendar, Text } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons/faCalendarDay';



interface DateRangePickerProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
	label?: string;
	description?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
}



export function DateRangePicker<T extends DateValue>(props: DateRangePickerProps<T>) {
	const { label, isRequired = false, description, errorMessage, ...otherProps } = props;

	let buttonRef = useRef<HTMLButtonElement>(null);
	
	return (
		<AriaDateRangePicker {...otherProps} isRequired={isRequired}>
		
			<Label>{label} {isRequired && <span>*</span>}</Label>
			
			<Button ref={buttonRef}>
				<DateInput slot="start">
					{(segment) => <DateSegment segment={segment} />}
				</DateInput>
				
				<span aria-hidden="true" className='react-aria-PickerSeparator'>–</span>
				
				<DateInput slot="end">
					{(segment) => <DateSegment segment={segment} />}
				</DateInput>
				
				<span aria-hidden="true" className='react-aria-PickerButton'>
					<FontAwesomeIcon icon={faCalendarDay} />
				</span>
			</Button>

			{description && <Text slot="description">{description}</Text>}
			<FieldError>{errorMessage}</FieldError>
		
			<Popover triggerRef={buttonRef}>
				<Dialog>
					<RangeCalendar>
						
						<header>
							<Button slot="previous">◀</Button>
							<Heading />
							<Button slot="next">▶</Button>
						</header>
						
						<CalendarGrid weekdayStyle='short'>
							<CalendarGridHeader>
								{(day) => <CalendarHeaderCell>{day.charAt(0).toUpperCase() + day.slice(1)}</CalendarHeaderCell>}
							</CalendarGridHeader>

							<CalendarGridBody>
								{(date) => <CalendarCell date={date} />}
							</CalendarGridBody>
						</CalendarGrid>
					
					</RangeCalendar>
				</Dialog>
			</Popover>
		
		</AriaDateRangePicker>
	);
}